import { ActivityStatusConstants } from "../constants/ActivityStatusConstants";
import { ActivityTypeConstants } from "../constants/ActivityTypeConstants";
import { MemberCategoryConstants } from "../constants/MemberCategoryConstants";
import { ActivityDTO, MemberDTO } from "../services/apiClient";

// USED BY:
// ComplianceOverview
// Helper function to check if member is in a dedicated category
export const isDedicatedCategory = (memberCategoryId: string): boolean => {
    return memberCategoryId === MemberCategoryConstants.DEDICATED_HUNTER_AND_OCCASIONAL_SPORTPERSON ||
        memberCategoryId === MemberCategoryConstants.DEDICATED_SPORTPERSON_AND_OCCASIONAL_HUNTER ||
        memberCategoryId === MemberCategoryConstants.DEDICATED_SPORTPERSON ||
        memberCategoryId === MemberCategoryConstants.DEDICATED_HUNTER ||
        memberCategoryId === MemberCategoryConstants.DEDICATED_HUNTER_AND_SPORTPERSON;
};

// USED BY:
// getComplianceActivityTypes
export const isdedicatedHunter = (memberCategoryId: string): boolean => {
    return memberCategoryId === MemberCategoryConstants.DEDICATED_HUNTER ||
        memberCategoryId === MemberCategoryConstants.DEDICATED_HUNTER_AND_OCCASIONAL_SPORTPERSON ||
        memberCategoryId === MemberCategoryConstants.DEDICATED_HUNTER_AND_SPORTPERSON;
}

// USED BY:
// getComplianceActivityTypes
export const isdedicatedSportperson = (memberCategoryId: string): boolean => {
    return memberCategoryId === MemberCategoryConstants.DEDICATED_SPORTPERSON ||
        memberCategoryId === MemberCategoryConstants.DEDICATED_SPORTPERSON_AND_OCCASIONAL_HUNTER ||
        memberCategoryId === MemberCategoryConstants.DEDICATED_HUNTER_AND_SPORTPERSON;
}

// USED BY:
// ComplianceOverview
// ComplianceHistoryOverview
// Helper function to determine what activities to show, considering certificate dates
export const getComplianceActivityTypes = (memberCategoryId: string, member?: MemberDTO | null) => {
    const activities = [];
    const currentDate = new Date();

    // Check if certificates were acquired in current compliance year
    const hunterCertInCurrentYear = member?.dateCertificateHunter && member?.dateOfBirth
        ? isCertificateAcquiredInCurrentYear(member.dateCertificateHunter, member.dateOfBirth)
        : false;

    const sportCertInCurrentYear = member?.dateCertificateSport && member?.dateOfBirth
        ? isCertificateAcquiredInCurrentYear(member.dateCertificateSport, member.dateOfBirth)
        : false;

    // Add hunt activities if member is a hunter (isNeeded depends on certificate acquisition OR always-compliant status)
    if (isdedicatedHunter(memberCategoryId)) {
        activities.push({
            type: 'Hunt Activities',
            requiredCount: 2,
            activityTypeId: ActivityTypeConstants.HUNT_RELATED,
            isNeeded: !(hunterCertInCurrentYear || member?.isAlwaysCompliant)
        });
    }

    // Add sport activities if member is a sportperson (isNeeded depends on certificate acquisition OR always-compliant status)
    if (isdedicatedSportperson(memberCategoryId)) {
        activities.push({
            type: 'Sport Activities',
            requiredCount: 2,
            activityTypeId: ActivityTypeConstants.SPORT_SHOOTING,
            isNeeded: !(sportCertInCurrentYear || member?.isAlwaysCompliant)
        });
    }

    return activities;
}

// USED BY:
// Documents.renderCertificatesTab (compute current status for HUNT/SPORT)
// Note: We only return 'Compliant' or 'Not compliant' (no 'Not needed').
export type ComplianceFlag = 'Compliant' | 'Not compliant';
export interface CurrentComplianceStatus {
    hunter?: ComplianceFlag;
    sport?: ComplianceFlag;
}

export const getCurrentComplianceStatus = (member: MemberDTO | null, items: ActivityDTO[]): CurrentComplianceStatus => {
    const status: CurrentComplianceStatus = {};
    if (!member) return status;

    const hunterNeeded = isdedicatedHunter(member.memberCategoryId!) &&
        !(
            (member.dateCertificateHunter && member.dateOfBirth
                ? isCertificateAcquiredInCurrentYear(member.dateCertificateHunter, member.dateOfBirth)
                : false) ||
            member.isAlwaysCompliant
        );

    const sportNeeded = isdedicatedSportperson(member.memberCategoryId!) &&
        !(
            (member.dateCertificateSport && member.dateOfBirth
                ? isCertificateAcquiredInCurrentYear(member.dateCertificateSport, member.dateOfBirth)
                : false) ||
            member.isAlwaysCompliant
        );

    // Default requirement is 2 per type
    const REQUIRED = 2;

    if (isdedicatedHunter(member.memberCategoryId!)) {
        if (!hunterNeeded) {
            status.hunter = 'Compliant';
        } else {
            const count = getActivityCountByType(items, ActivityTypeConstants.HUNT_RELATED);
            status.hunter = count >= REQUIRED ? 'Compliant' : 'Not compliant';
        }
    }

    if (isdedicatedSportperson(member.memberCategoryId!)) {
        if (!sportNeeded) {
            status.sport = 'Compliant';
        } else {
            const count = getActivityCountByType(items, ActivityTypeConstants.SPORT_SHOOTING);
            status.sport = count >= REQUIRED ? 'Compliant' : 'Not compliant';
        }
    }

    return status;
};

// USED BY:
// ComplianceOverview
export function getComplianceTitle(dobInput: string | Date | null | undefined, now: Date = new Date()): string | null {
    if (!dobInput) return null;

    const dob = new Date(dobInput);
    if (isNaN(dob.getTime())) return null;

    // Use local date parts (not UTC) — dateOfBirth arrives as an offset-less
    // ISO datetime (e.g. "1990-08-01T00:00:00"), which the browser parses as
    // local time. Reading it back with getUTC* shifts it a day earlier in any
    // positive-offset timezone, producing an off-by-one-month title.
    const birthMonth = dob.getMonth(); // 0..11
    const targetYear =
        now.getMonth() > birthMonth ? now.getFullYear() + 1 : now.getFullYear();

    const label = new Date(targetYear, birthMonth, 1);
    const mon = label.toLocaleString("en-US", { month: "short" }).toUpperCase();

    return `COMPLIANCE ${mon} ${label.getFullYear()}`;
}

// USED BY:
// ComplianceOverview
// Helper function to count activities by type (only approved ones)
// ActivityTypeId: ActivityTypeConstants.HUNT_RELATED,
// ActivityTypeId: ActivityTypeConstants.SPORT_SHOOTING,
export const getActivityCountByType = (items: ActivityDTO[], activityTypeId: string): number => {
    return items.filter(item => item.activityTypeId === activityTypeId).length;
};




// Helper function to get compliance year boundaries based on member's date of birth
export const getComplianceYearBoundaries = (dateOfBirth: Date | string | null | undefined, currentDate: Date = new Date()) => {
    if (!dateOfBirth) return null;

    const dob = new Date(dateOfBirth);
    if (isNaN(dob.getTime())) return null;

    const birthMonth = dob.getMonth(); // 0-11
    const monthAfterBirthday = birthMonth === 11 ? 0 : birthMonth + 1;
    const yearForStart = currentDate.getFullYear();

    // Start: 1st day of the month after birthday month
    const complianceYearStart = new Date(yearForStart, monthAfterBirthday, 1);

    // End: Last day of birthday month next year
    const endYear = monthAfterBirthday === 0 ? yearForStart : yearForStart + 1;
    const lastDayOfBirthdayMonth = new Date(endYear, birthMonth + 1, 0).getDate();
    const complianceYearEnd = new Date(endYear, birthMonth, lastDayOfBirthdayMonth, 23, 59, 59);

    // Adjust if current date suggests we should be looking at previous compliance year
    // Exception: December birthdays (birthMonth = 11) have compliance years that align with calendar year (Jan-Dec),
    // so they never need backward adjustment
    if (currentDate.getMonth() <= birthMonth && birthMonth !== 11) {
        complianceYearStart.setFullYear(complianceYearStart.getFullYear() - 1);
        const adjustedEndYear = monthAfterBirthday === 0 ? complianceYearStart.getFullYear() : complianceYearStart.getFullYear() + 1;
        const adjustedLastDay = new Date(adjustedEndYear, birthMonth + 1, 0).getDate();
        complianceYearEnd.setFullYear(adjustedEndYear);
        complianceYearEnd.setMonth(birthMonth);
        complianceYearEnd.setDate(adjustedLastDay);
    }

    return { start: complianceYearStart, end: complianceYearEnd };
};

// Helper function to check if a certificate was acquired within the current compliance year
export const isCertificateAcquiredInCurrentYear = (certificateDate: Date | string | null | undefined, dateOfBirth: Date | string | null | undefined): boolean => {
    if (!certificateDate || !dateOfBirth) return false;

    const certDate = new Date(certificateDate);
    if (isNaN(certDate.getTime())) return false;

    const boundaries = getComplianceYearBoundaries(dateOfBirth);
    if (!boundaries) return false;

    return certDate >= boundaries.start && certDate <= boundaries.end;
};

// Helper function to group historical activities by compliance year
export const groupActivitiesByComplianceYear = (activities: ActivityDTO[]) => {
    const grouped: { [key: string]: ActivityDTO[] } = {};

    activities.forEach(activity => {
        const year = activity.complianceYear || 'unknown';
        if (!grouped[year]) {
            grouped[year] = [];
        }
        grouped[year].push(activity);
    });

    return grouped;
};